import React, { useEffect, useMemo, useState } from 'react';

import { ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import { InheritedConfigSettingCallouts } from '../../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../../hooks/useChildInheritedConfigEdit';
import { isConfigInheritedForMerchant } from '../../../util/configInheritance';
import { inheritableLabelsFromConfigNames } from '../../../util/parentSettingSave';
import { ConfigResponse } from '../../../store/modules/merchant/types';
import { useMerchant } from '../../../contexts/MerchantContext';

type Props = {
  readOnly?: boolean;
  isSaving?: boolean;
  onSave: (paylinksRecurringCheckbox: boolean) => Promise<void>;
  paylinksRecurringCheckboxConfig?: ConfigResponse<boolean>;
};

function getServerPaylinksRecurringCheckbox(config?: ConfigResponse<boolean>): boolean {
  return Boolean(config?.value);
}

export const PaylinkRecurringCheckboxContent = ({
  onSave,
  paylinksRecurringCheckboxConfig,
  readOnly = false,
  isSaving = false,
}: Props) => {
  const { merchantId } = useMerchant();
  const settingInherited = isConfigInheritedForMerchant(
    merchantId,
    paylinksRecurringCheckboxConfig,
  );
  const {
    showInheritedCallouts,
    isCustomizingInherited,
    controlsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
    isChildInheritedSaveEnabled,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);

  const serverValue = getServerPaylinksRecurringCheckbox(paylinksRecurringCheckboxConfig);
  const [paylinksRecurringCheckbox, setPaylinksRecurringCheckbox] = useState(serverValue);

  useEffect(() => {
    setPaylinksRecurringCheckbox(serverValue);
    resetCustomizing();
  }, [serverValue, paylinksRecurringCheckboxConfig?.configId, resetCustomizing]);

  const isFormDirty = paylinksRecurringCheckbox !== serverValue;
  const isSaveBarOpen = isChildInheritedSaveBarOpen(isFormDirty);

  const resetValues = () => {
    setPaylinksRecurringCheckbox(serverValue);
    resetCustomizing();
  };

  const handleSave = async () => {
    await onSave(paylinksRecurringCheckbox);
    resetCustomizing();
  };

  const pageInheritableLabels = useMemo(
    () => inheritableLabelsFromConfigNames(['PAYLINKS_RECURRING_CHECKBOX']),
    [],
  );

  const saveBarInheritance = getSaveBarInheritanceProps(
    isSaveBarOpen,
    isFormDirty ? pageInheritableLabels : [],
    pageInheritableLabels,
  );

  return (
    <>
      {showInheritedCallouts ? (
        <InheritedConfigSettingCallouts
          isCustomizingInherited={isCustomizingInherited}
          readOnly={readOnly}
          onCustomize={onCustomize}
        />
      ) : null}
      <ConfigSwitchSection
        title="Save cards for future transactions"
        description={
          'Enable the option to save cards for future transactions when creating a Paylink.' +
          ' When enabled, all Paylinks will require saving new customers, or using existing customers.'
        }
        isChecked={paylinksRecurringCheckbox}
        onToggle={() => setPaylinksRecurringCheckbox(!paylinksRecurringCheckbox)}
        disabled={controlsDisabled}
        switchTestId="PAYLINKS_RECURRING_CHECKBOX"
      />
      <ParentAwareSaveBar
        disableSave={readOnly || isSaving || !isChildInheritedSaveEnabled(isFormDirty)}
        saveLoading={isSaving}
        show={isSaveBarOpen && !readOnly}
        onCancel={() => resetValues()}
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarInheritance.detailTextOverride}
        onSave={handleSave}
      />
    </>
  );
};
